import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import './RightPathPage.css';

const RightPathPage = () => {
  const navigate = useNavigate();
  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const catalogCategories = [
    {
      title: 'Technical Certifications',
      count: 15,
      color: '#1B2A4E',
      courses: ['CompTIA A+', 'CompTIA Network+', 'CompTIA Security+', 'CompTIA Cloud+', 'CompTIA Data+', 'CompTIA Linux+', 'CompTIA PenTest+', 'Plus additional industry-recognized credentials'],
    },
    {
      title: 'AI & Future-Ready Learning',
      count: 15,
      color: '#C9A227',
      courses: ['Intro to Artificial Intelligence', 'Intro to Generative AI', 'Understanding Large Language Models', 'Prompt Engineering & Optimization', 'Generative AI for Business Innovation', 'Building AI-Powered Chatbots'],
    },
    {
      title: 'Digital & Workplace Skills',
      count: 9,
      color: '#5A6C8A',
      courses: ['Digital Literacy', 'Microsoft Office Tips & Tricks', 'Durable Skills', 'Office-Based Technology', 'Marketing with Consistency', 'Foundational Employability'],
    },
    {
      title: 'Data, Analytics & Productivity',
      count: 4,
      color: '#1B2A4E',
      courses: ['Intro to Data Analytics', 'Advanced Data Analytics', 'Intro to Excel', 'Advanced Excel'],
    },
    {
      title: 'CRM & Project Management',
      count: 12,
      color: '#C9A227',
      courses: ['Salesforce Administrator', 'Salesforce App Builder', 'Salesforce Developer', 'Salesforce Marketing Cloud', 'CAPM', 'PMP', 'PMI-ACP', 'Plus additional CRM & PM pathways'],
    },
    {
      title: 'Healthcare Pathways',
      count: 10,
      color: '#5A6C8A',
      courses: ['Certified Medical Assistant (CMA)', 'Patient Care Technician (PCT)', 'Certified Nursing Assistant (CNA)', 'Sterile Processing', 'Surgical Tech'],
      comingSoon: ['CCMA Program Imaging (In Development)', 'Dental Assistant (Coming 2027)', 'Pharmacy Tech (Coming 2027)'],
    },
    {
      title: 'Leadership & Employee Dev.',
      count: 8,
      color: '#1B2A4E',
      courses: ['Leadership & Management', 'Job Readiness', 'Manager Development', 'Employee Growth Programs', 'Frontline Supervisor Training', 'Internal Talent Advancement'],
    },
    {
      title: 'Skilled Trades',
      count: 4,
      color: '#C9A227',
      courses: [],
      comingSoon: ['HVAC (Q4 2026)', 'Electrician (Q4 2026)', 'Plumbing (Q4 2026)', 'CDL (Q4 2026)'],
      allComingSoon: true,
    },
  ];

  const CatalogGrid = () => (
    <div className="rp-catalog-grid">
      {catalogCategories.map((cat, i) => (
        <div 
          key={i} 
          className={`rp-catalog-card ${expandedCategory === i ? 'expanded' : ''}`}
          data-testid={`catalog-category-${i}`}
        >
          <div 
            className="rp-catalog-card-header" 
            onClick={() => setExpandedCategory(expandedCategory === i ? null : i)}
          >
            <div className="rp-catalog-card-info">
              <h3>{cat.title}</h3>
              <span className="rp-catalog-count">{cat.count} offerings</span>
            </div>
            {cat.allComingSoon && <span className="rp-catalog-soon-badge">Coming Q4 2026</span>}
            <ChevronDown size={18} className={`rp-catalog-chevron ${expandedCategory === i ? 'rotated' : ''}`} />
          </div>
          {expandedCategory === i && (
            <div className="rp-catalog-card-body">
              {cat.courses.length > 0 && (
                <ul className="rp-catalog-courses">
                  {cat.courses.map((course, j) => (
                    <li key={j}>{course}</li>
                  ))}
                </ul>
              )}
              {cat.comingSoon && cat.comingSoon.length > 0 && (
                <ul className="rp-catalog-courses coming-soon">
                  {cat.comingSoon.map((course, j) => (
                    <li key={j} className="rp-coming-soon-item">{course}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const benefits = [
    {
      title: 'Strategic Alignment',
      description: 'Align workforce development with your strategic business goals and objectives.'
    },
    {
      title: 'Talent Pipeline',
      description: 'Build sustainable, role-ready talent pipelines for current and future needs.'
    },
    {
      title: 'Upskilling & Reskilling',
      description: 'Develop existing employees with future-focused skills and capabilities.'
    },
    {
      title: 'Future-Proof Workforce',
      description: 'Ensure your workforce remains adaptable as your industry evolves.'
    },
    {
      title: 'AI-Powered Learning',
      description: 'Intelligent technology that personalizes pathways, recommends skills, and adapts to individual learning styles.'
    },
    {
      title: 'Community Ecosystem',
      description: 'Network of local organizations and services supporting workforce success through wraparound support.'
    },
    {
      title: 'Measurable Outcomes',
      description: 'Track progress with data-driven insights, ROI metrics, and continuous performance measurement.'
    },
    {
      title: 'Retention & Engagement',
      description: 'Reduce turnover and boost engagement by investing in employee growth and career development.'
    },
    {
      title: 'Career Coaching',
      description: 'Expert coaches guide employees through their development journey and connect learning to advancement.'
    }
  ];

  const features = [
    'Comprehensive skills gap analysis',
    'Custom career pathway design',
    'Leadership development programs',
    'Performance management systems',
    'Succession planning frameworks',
    'Talent analytics and reporting',
    'AI-powered personalized learning paths',
    'Intelligent skills recommendation engine',
    'Adaptive learning technology',
    'Curated learning marketplace with vetted programs',
    'Expert coaching and career guidance',
    'Certificate and credential programs'
  ];

  const aiLearningPillars = [
    {
      title: 'Personalized Learning Paths',
      description: 'AI analyzes skills gaps and career aspirations to create individualized development journeys—ensuring every employee gets the right training at the right time.'
    },
    {
      title: 'Adaptive Content Delivery',
      description: 'Intelligent systems adjust learning pace, format, and complexity based on individual progress and learning style, maximizing retention and engagement.'
    },
    {
      title: 'Skills Intelligence',
      description: 'Machine learning identifies emerging skill requirements, maps competencies to roles, and proactively recommends development opportunities before gaps impact performance.'
    },
    {
      title: 'Expert Coaching & Support',
      description: 'AI-assisted career coaches help employees navigate their development journey, connect learning to career advancement, and maximize their growth potential.'
    }
  ];

  const phases = [
    {
      number: '01',
      title: 'Discovery & Assessment',
      description: 'Analyze current workforce capabilities, identify skill gaps, and understand strategic business needs.'
    },
    {
      number: '02',
      title: 'Program Design',
      description: 'Create customized development pathways, learning curricula, and talent frameworks tailored to your organization.'
    },
    {
      number: '03',
      title: 'Implementation',
      description: 'Deploy training programs, implement systems, and provide hands-on support for seamless adoption.'
    },
    {
      number: '04',
      title: 'Measurement & Optimization',
      description: 'Track progress against KPIs, measure ROI, and continuously refine programs for maximum impact.'
    }
  ];

  return (
    <div className="right-path-page">
      <PageSEO
        title="The Right Path Program"
        path="/the-right-path"
        description="The R6 Collective's flagship workforce development program. Build sustainable talent pipelines through strategic upskilling, reskilling, and career pathway development."
      />
      <Header />
      
      {/* Hero Section with Dynamic Paths Background */}
      <section className="rp-hero">
        {/* Animated SVG Multiple Paths for Different People - Brand Colors */}
        <div className="rp-path-bg">
          <svg viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice">
            {/* Path 1 - Using Primary Gold lighter */}
            <path 
              className="diversity-path path-1"
              d="M-50,580 Q150,540 300,480 T550,400 T750,340 T900,300"
              fill="none"
              stroke="rgba(201, 162, 39, 0.2)"
              strokeWidth="4"
            />
            <path 
              className="diversity-path-animated path-1"
              d="M-50,580 Q150,540 300,480 T550,400 T750,340 T900,300"
              fill="none"
              stroke="rgba(201, 162, 39, 0.6)"
              strokeWidth="4"
              strokeDasharray="12 18"
            />
            
            {/* Path 2 - Using Secondary Slate */}
            <path 
              className="diversity-path path-2"
              d="M150,620 Q280,570 420,510 T650,420 T850,350 T900,320"
              fill="none"
              stroke="rgba(90, 108, 138, 0.25)"
              strokeWidth="4"
            />
            <path 
              className="diversity-path-animated path-2"
              d="M150,620 Q280,570 420,510 T650,420 T850,350 T900,320"
              fill="none"
              stroke="#5A6C8A"
              strokeWidth="4"
              strokeDasharray="12 18"
            />
            
            {/* Path 3 - Using Primary Navy lighter */}
            <path 
              className="diversity-path path-3"
              d="M-30,450 Q150,430 320,400 T580,360 T780,320 T900,290"
              fill="none"
              stroke="rgba(27, 42, 78, 0.3)"
              strokeWidth="4"
            />
            <path 
              className="diversity-path-animated path-3"
              d="M-30,450 Q150,430 320,400 T580,360 T780,320 T900,290"
              fill="none"
              stroke="rgba(90, 108, 138, 0.8)"
              strokeWidth="4"
              strokeDasharray="12 18"
            />
            
            {/* Path 4 - Using White/Light */}
            <path 
              className="diversity-path path-4"
              d="M500,620 Q600,560 700,490 T850,380 T900,330"
              fill="none"
              stroke="rgba(255, 255, 255, 0.15)"
              strokeWidth="4"
            />
            <path 
              className="diversity-path-animated path-4"
              d="M500,620 Q600,560 700,490 T850,380 T900,330"
              fill="none"
              stroke="rgba(255, 255, 255, 0.5)"
              strokeWidth="4"
              strokeDasharray="12 18"
            />
            
            {/* Path 5 - Using Gold/Slate blend */}
            <path 
              className="diversity-path path-5"
              d="M350,620 Q450,550 560,480 T750,380 T900,310"
              fill="none"
              stroke="rgba(180, 155, 80, 0.2)"
              strokeWidth="4"
            />
            <path 
              className="diversity-path-animated path-5"
              d="M350,620 Q450,550 560,480 T750,380 T900,310"
              fill="none"
              stroke="rgba(180, 155, 80, 0.7)"
              strokeWidth="4"
              strokeDasharray="12 18"
            />
            
            {/* Path 6 - Using Warm Beige tones */}
            <path 
              className="diversity-path path-6"
              d="M1440,580 Q1300,520 1150,450 T950,360 T900,320"
              fill="none"
              stroke="rgba(245, 243, 240, 0.15)"
              strokeWidth="4"
            />
            <path 
              className="diversity-path-animated path-6"
              d="M1440,580 Q1300,520 1150,450 T950,360 T900,320"
              fill="none"
              stroke="rgba(245, 243, 240, 0.5)"
              strokeWidth="4"
              strokeDasharray="12 18"
            />

            {/* THE RIGHT PATH - Golden convergence path leading to success */}
            <path 
              className="right-path-main"
              d="M900,310 Q1020,280 1140,230 T1340,140 T1500,50"
              fill="none"
              stroke="rgba(201, 162, 39, 0.4)"
              strokeWidth="8"
            />
            <path 
              className="right-path-animated"
              d="M900,310 Q1020,280 1140,230 T1340,140 T1500,50"
              fill="none"
              stroke="#C9A227"
              strokeWidth="8"
              strokeDasharray="15 25"
            />
            
            {/* Convergence point - where all paths meet The Right Path */}
            <circle className="convergence-point" cx="900" cy="310" r="25" fill="rgba(201, 162, 39, 0.3)" />
            <circle className="convergence-point-inner" cx="900" cy="310" r="15" fill="#C9A227" />
            <circle className="convergence-glow" cx="900" cy="310" r="35" fill="none" stroke="#C9A227" strokeWidth="2" />
            
            {/* Person icons at starting points - brand colors */}
            <circle className="person-icon" cx="20" cy="560" r="10" fill="#C9A227" />
            <circle className="person-head" cx="20" cy="552" r="4" fill="#1B2A4E" />
            
            <circle className="person-icon" cx="170" cy="600" r="10" fill="#5A6C8A" />
            <circle className="person-head" cx="170" cy="592" r="4" fill="#1B2A4E" />
            
            <circle className="person-icon" cx="30" cy="440" r="10" fill="rgba(90, 108, 138, 0.8)" />
            <circle className="person-head" cx="30" cy="432" r="4" fill="#1B2A4E" />
            
            <circle className="person-icon" cx="520" cy="600" r="10" fill="rgba(255, 255, 255, 0.7)" />
            <circle className="person-head" cx="520" cy="592" r="4" fill="#1B2A4E" />
            
            <circle className="person-icon" cx="370" cy="600" r="10" fill="rgba(180, 155, 80, 0.9)" />
            <circle className="person-head" cx="370" cy="592" r="4" fill="#1B2A4E" />
            
            <circle className="person-icon" cx="1410" cy="560" r="10" fill="rgba(245, 243, 240, 0.7)" />
            <circle className="person-head" cx="1410" cy="552" r="4" fill="#1B2A4E" />
            
            {/* Milestone markers on The Right Path */}
            <circle className="milestone" cx="1020" cy="280" r="8" fill="#C9A227" />
            <circle className="milestone" cx="1150" cy="230" r="8" fill="#C9A227" />
            <circle className="milestone" cx="1280" cy="170" r="8" fill="#C9A227" />
            <circle className="milestone" cx="1410" cy="100" r="8" fill="#C9A227" />
          </svg>
        </div>
        
        <div className="rp-hero-container">
          <div className="rp-hero-badge">Strategic Workforce Development</div>
          <h1 className="rp-hero-title">
            The Right Path
          </h1>
          <p className="rp-hero-subtitle">
            Every person's journey is unique. From career changers to early professionals, from veterans to senior leaders—our proven framework guides people at every stage toward sustainable success through upskilling and reskilling.
          </p>
          <div className="rp-hero-buttons">
            <button className="btn btn-primary" onClick={() => navigate('/assessment')}>
              Take the Assessment
            </button>
            <button className="btn btn-secondary-light" onClick={() => {
              const element = document.querySelector('#rp-overview');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}>
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section id="rp-overview" className="rp-overview">
        <div className="rp-container">
          <div className="rp-overview-content">
            <div className="rp-overview-text">
              <h2 className="rp-section-title">A Comprehensive Approach to Talent Development</h2>
              <p className="rp-text">
                The Right Path addresses both talent acquisition and talent development—helping you attract new talent while upskilling and reskilling your existing employees.
              </p>
              <p className="rp-text">
                Our program combines strategic workforce planning, skills development, and organizational capability building to create a talent ecosystem that drives sustainable competitive advantage.
              </p>
              <p className="rp-text">
                Built on the foundation of The Right 6™ Framework, The Right Path ensures your workforce remains adaptable, capable, and future-proof as your industry evolves.
              </p>
            </div>
            <div className="rp-overview-stats">
              <div className="rp-stat-card">
                <div className="rp-stat-number">2x</div>
                <div className="rp-stat-label">Internal Mobility within 24 months</div>
              </div>
              <div className="rp-stat-card">
                <div className="rp-stat-number">71%</div>
                <div className="rp-stat-label">Productivity Gains post-training</div>
              </div>
              <div className="rp-stat-card">
                <div className="rp-stat-number">3x</div>
                <div className="rp-stat-label">ROI per dollar invested annually</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Structure Section */}
      <section className="rp-program-structure">
        <div className="rp-container">
          {/* Header */}
          <div className="rp-ps-header">
            <div className="rp-ps-header-row">
              <span className="rp-ps-lever-badge">GROWTH LEVER 1 · WORKFORCE DEVELOPMENT</span>
              <div className="rp-ps-logo">
                <div className="rp-ps-logo-grid">
                  <span></span><span></span>
                  <span></span><span className="gold"></span>
                </div>
              </div>
            </div>
            <h2 className="rp-ps-title">The Right Path — Workforce Development Program Structure</h2>
            <span className="rp-ps-title-underline"></span>
            <p className="rp-ps-subtitle">Structured talent solutions tailored for growing businesses, community organizations, and workforce boards.</p>
          </div>

          {/* Phase 1 Bar */}
          <div className="rp-ps-phase-bar">
            <div className="rp-ps-phase-badge">PHASE 1</div>
            <span className="rp-ps-phase-title">ASSESSMENT</span>
            <span className="rp-ps-phase-desc">Organizational readiness, culture analysis, and talent gap identification.</span>
            <span className="rp-ps-applies-all">
              <span className="rp-ps-check-icon"></span>
              Applies to all groups
            </span>
          </div>

          {/* Three Group Cards - Unified Container */}
          <div className="rp-ps-groups-container">
            <div className="rp-ps-group-col">
              <div className="rp-ps-group-number">01</div>
              <h3 className="rp-ps-group-title">Growing Companies</h3>
              <p className="rp-ps-group-desc">Building internal workforce pipelines to solve talent shortages and retention challenges.</p>
              <div className="rp-ps-group-tag-bottom">
                <div className="rp-ps-group-tag wraparound">
                  <span className="rp-ps-tag-dot"></span>
                  Wraparound Services
                </div>
              </div>
            </div>

            <div className="rp-ps-divider"></div>

            <div className="rp-ps-group-col">
              <div className="rp-ps-group-number">02</div>
              <h3 className="rp-ps-group-title">Communities & Nonprofits</h3>
              <p className="rp-ps-group-desc">Serving multiple client groups across training areas to drive local economic impact.</p>
              <div className="rp-ps-group-tag-bottom">
                <div className="rp-ps-group-tag wraparound">
                  <span className="rp-ps-tag-dot"></span>
                  Wraparound Services
                </div>
              </div>
            </div>

            <div className="rp-ps-divider"></div>

            <div className="rp-ps-group-col">
              <div className="rp-ps-group-number">03</div>
              <h3 className="rp-ps-group-title">Workforce Boards</h3>
              <p className="rp-ps-group-desc">Seeking reliable training providers to grant access to high-quality training for constituents.</p>
              <div className="rp-ps-group-tag-bottom">
                <div className="rp-ps-group-tag no-wraparound">
                  <span className="rp-ps-tag-icon"></span>
                  No Wraparound Services
                </div>
              </div>
            </div>
          </div>

          {/* Arrows pointing up */}
          <div className="rp-ps-arrows-row">
            <div className="rp-ps-arrow-up left"></div>
            <div className="rp-ps-arrow-up right"></div>
          </div>

          {/* Phase 2 Sections */}
          <div className="rp-ps-phase2-row">
            <div className="rp-ps-phase2-left">
              <div className="rp-ps-phase2-header">
                <div className="rp-ps-phase-badge">PHASE 2</div>
                <span className="rp-ps-phase-title-dark">DELIVERY & EXECUTION</span>
                <span className="rp-ps-flexible">Flexible Deployment Models (For Groups 1 & 2)</span>
              </div>
              <div className="rp-ps-delivery-options">
                <div className="rp-ps-delivery-option">DIY Framework</div>
                <div className="rp-ps-delivery-option">Train & Transfer (TNT)</div>
                <div className="rp-ps-delivery-option">Full-Scale Program</div>
              </div>
            </div>

            <div className="rp-ps-phase2-right">
              <div className="rp-ps-phase2-header-dark">
                <div className="rp-ps-phase-badge dark">PHASE 2</div>
                <span className="rp-ps-phase-title-white">DELIVERY</span>
              </div>
              <div className="rp-ps-delivery-only">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                Training Delivery Only
              </div>
            </div>
          </div>

          {/* Footer Bar */}
          <div className="rp-ps-footer-bar">
            R6 Collective | Workforce Development Structure
          </div>
        </div>
      </section>

      {/* Course Catalog Section */}
      <section className="rp-catalog" data-testid="rp-catalog">
        <div className="rp-container">
          <div className="rp-catalog-header">
            <div className="rp-catalog-badge">THE RIGHT PATH CATALOG</div>
            <h2 className="rp-section-title text-center">77+ Workforce Offerings Across 8 Categories</h2>
            <p className="rp-section-subtitle">Industry-recognized credentials, professional development, healthcare pathways, AI-enabled learning, and skilled-trades programs — built for immediate workforce needs and long-term economic mobility.</p>
          </div>
          <CatalogGrid />
        </div>
      </section>

      {/* Who This Serves */}
      <section className="rp-serves" data-testid="rp-serves">
        <div className="rp-container">
          <h2 className="rp-section-title text-center">Who the Catalog Serves</h2>
          <div className="rp-serves-grid">
            <div className="rp-serves-card">
              <h3>Career Starters</h3>
              <p>Building foundational skills and earning industry-recognized credentials for workforce entry.</p>
            </div>
            <div className="rp-serves-card">
              <h3>Incumbent Workers</h3>
              <p>Upskilling and reskilling for career advancement, role changes, and future-ready capabilities.</p>
            </div>
            <div className="rp-serves-card">
              <h3>Frontline + Supervisors</h3>
              <p>Leadership development, management training, and employee growth programs for team leads.</p>
            </div>
            <div className="rp-serves-card">
              <h3>Employers + Cities</h3>
              <p>Building workforce portfolios aligned to employer demand and community economic goals.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="rp-benefits">
        <div className="rp-container">
          <h2 className="rp-section-title text-center">Why Choose The Right Path?</h2>
          <p className="rp-section-subtitle">Transform your workforce with strategic, sustainable development</p>
          <div className="rp-benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="rp-benefit-card">
                <h3 className="rp-benefit-title">{benefit.title}</h3>
                <p className="rp-benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="rp-features">
        <div className="rp-container">
          <h2 className="rp-section-title text-center">Program Features</h2>
          <p className="rp-section-subtitle">Comprehensive solutions for every stage of workforce development</p>
          <div className="rp-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="rp-feature-item">
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Learning Section */}
      <section className="rp-ai-learning">
        <div className="rp-container">
          <div className="rp-ai-header">
            <div className="rp-ai-badge">Powered by AI</div>
            <h2 className="rp-section-title text-center">Intelligent Learning That Drives Results</h2>
            <p className="rp-section-subtitle">AI-powered workforce development that adapts to your people and your business</p>
          </div>
          <div className="rp-ai-grid">
            {aiLearningPillars.map((pillar, index) => (
              <div key={index} className="rp-ai-card">
                <h3 className="rp-ai-title">{pillar.title}</h3>
                <p className="rp-ai-description">{pillar.description}</p>
              </div>
            ))}
          </div>
          <div className="rp-ai-stats">
            <div className="rp-ai-stat">
              <span className="rp-ai-stat-number">90K</span>
              <span className="rp-ai-stat-label">Cumulative role changes over the past decade</span>
            </div>
            <div className="rp-ai-stat">
              <span className="rp-ai-stat-number">$487M</span>
              <span className="rp-ai-stat-label">Cumulative wage growth over the past decade</span>
            </div>
            <div className="rp-ai-stat">
              <span className="rp-ai-stat-number">80%</span>
              <span className="rp-ai-stat-label">Resilient-skill programs in current learner cohorts</span>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="rp-process">
        <div className="rp-container">
          <h2 className="rp-section-title text-center">Our Process</h2>
          <p className="rp-section-subtitle">A proven four-phase approach to workforce transformation</p>
          <div className="rp-process-grid">
            {phases.map((phase, index) => (
              <div key={index} className="rp-process-card">
                <div className="rp-process-number">{phase.number}</div>
                <h3 className="rp-process-title">{phase.title}</h3>
                <p className="rp-process-description">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="rp-cta">
        <div className="rp-container">
          <div className="rp-cta-content">
            <h2 className="rp-cta-title">Ready to Transform Your Workforce?</h2>
            <p className="rp-cta-text">
              Start with our comprehensive assessment to understand your current talent architecture and identify opportunities for growth.
            </p>
            <div className="rp-cta-buttons">
              <button className="btn btn-primary" onClick={() => navigate('/assessment')}>
                Take the Assessment
              </button>
              <button className="btn btn-outline" onClick={() => window.open('https://form.jotform.com/260186646305054', '_blank')}>
                Schedule a Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RightPathPage;